import { useEffect, useState, useCallback, useRef } from "react";
import { authApiV2 } from "../axiosWrapper";
import { 
    trackExceptionWithContext
} from '../services/application-insights/appInsights';
import { 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../services/application-insights/appInsightsConstants';

export const useRateLimitViolation = () => {
    const [isUserLimitedEndTime, setIsUserLimitEndTime] = useState(null as Date | null);
    const [isCheckingRateLimit, setIsCheckingRateLimit] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const retryCountRef = useRef(0);
    const maxRetries = 3;
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const checkRateLimitViolation = useCallback(async () => {
        try {
            const response = await authApiV2.get("/active-rate-limit-violation");
            
            if (response.data) {
                const { endsAtUtc } = response.data;
                const endTime = new Date(endsAtUtc);
                const now = new Date();

                if (endTime > now) {
                    setIsUserLimitEndTime(endTime);
                } else {
                    setIsUserLimitEndTime(null);
                }
            } else {
                setIsUserLimitEndTime(null);
            }

            // Reset retry count and error on success
            retryCountRef.current = 0;
            setError(null);

        } catch (err) {
            const error = err instanceof Error ? err : new Error('Rate limit check failed');
            setError(error.message);
            retryCountRef.current++;

            // Only log exceptions, not regular rate limit checks
            trackExceptionWithContext(
                error,
                {
                    component: AppInsightsComponents.RATE_LIMIT_HOOK,
                    additionalData: {
                        errorType: AppInsightsErrorTypes.RATE_LIMIT_ERROR,
                        retryCount: retryCountRef.current,
                        maxRetries,
                        willRetry: retryCountRef.current < maxRetries
                    }
                }
            );

            // Implement exponential backoff for retries
            if (retryCountRef.current < maxRetries) {
                const retryDelay = Math.pow(2, retryCountRef.current) * 1000; // 1s, 2s, 4s
                
                retryTimeoutRef.current = setTimeout(() => {
                    checkRateLimitViolation();
                }, retryDelay);
            } else {
                // Max retries exceeded, keep the error state but still set loading to false
                setIsUserLimitEndTime(null);
            }
        } finally {
            if (retryCountRef.current === 0 || retryCountRef.current >= maxRetries) {
                setIsCheckingRateLimit(false);
            }
        }
    }, [isUserLimitedEndTime]);

    useEffect(() => {
        checkRateLimitViolation();

        // Cleanup timeout on unmount
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, [checkRateLimitViolation]);

    return { 
        isUserLimitedEndTime, 
        isCheckingRateLimit, 
        error,
        retryCheck: checkRateLimitViolation 
    };
};
